var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { RendererSystem } from "../../rendering/renderer/RendererSystem";
import { TrainingVideoLoadingStateRequestMessage, TrainingVideoLoadingStateResponseMessage, TrainingVideoPlaybackMessage, TrainingVideoStateChangedMessage } from "flavr-common";
import { Vector3 } from "flavr-math";
import { cacheService } from "../../cache";
export class TrainingVideoSystem extends RendererSystem {
    constructor(rendererModule) {
        super(rendererModule, "TrainingVideoSystem");
        this.trainingVideoRenderables = new Map();
        /**
         * In-memory map from video source URL to blob: URL for the current session.
         * The actual Blob data is persisted in IndexedDB via cacheService so it
         * survives full page reloads.
         */
        this.videoBlobUrls = new Map();
        this.handleTrainingVideoLoadingStateRequestMessage = (_msg, askId) => {
            const numberOfVideos = this.trainingVideoRenderables.size;
            let numberOfVideosLoaded = 0;
            let numberOfFailedVideos = 0;
            this.trainingVideoRenderables.forEach((renderable) => {
                if (renderable === null || renderable === void 0 ? void 0 : renderable.isVideoLoaded()) {
                    numberOfVideosLoaded++;
                }
                else if (renderable === undefined) {
                    numberOfFailedVideos++;
                }
            });
            this.renderer.getConnection().answer(askId, new TrainingVideoLoadingStateResponseMessage(numberOfVideos, numberOfVideosLoaded, numberOfFailedVideos));
        };
        // message handlers
        this.handleTrainingVideoPlaybackMessage = (msg) => {
            const renderable = this.trainingVideoRenderables.get(msg.trainingVideoId);
            if (renderable !== undefined) {
                switch (msg.command) {
                    case "play":
                        void renderable.play();
                        break;
                    case "pause":
                        renderable.pause();
                        break;
                    case "stop":
                        renderable.stop();
                        break;
                    case "hide":
                        renderable.setVisible(false);
                        break;
                    case "show":
                        renderable.setVisible(true);
                        break;
                    default:
                        console.warn(`[Training] Unknown command received for TrainingVideoPlaybackMessage: ${msg.command}`);
                        break;
                }
                if (msg.newPostion !== undefined) {
                    renderable.owner.worldPosition = new Vector3().fromArray(msg.newPostion);
                }
            }
        };
        this.renderer.getConnection().subscribeByType(TrainingVideoPlaybackMessage, this.handleTrainingVideoPlaybackMessage);
        this.renderer.getConnection().subscribeForAskRequest(TrainingVideoLoadingStateRequestMessage, this.handleTrainingVideoLoadingStateRequestMessage);
        const handleKeyDown = (event) => {
            switch (event.key) {
                case "c":
                    // play all training videos
                    this.trainingVideoRenderables.forEach((renderable) => {
                        void (renderable === null || renderable === void 0 ? void 0 : renderable.play());
                    });
                    break;
                case "v":
                    // pause all training videos
                    this.trainingVideoRenderables.forEach((renderable) => {
                        renderable === null || renderable === void 0 ? void 0 : renderable.pause();
                    });
                    break;
                case "b":
                    // stop all training videos
                    this.trainingVideoRenderables.forEach((renderable) => {
                        renderable === null || renderable === void 0 ? void 0 : renderable.stop();
                    });
                    break;
                case "n":
                    // hide all training videos
                    this.trainingVideoRenderables.forEach((renderable) => {
                        renderable === null || renderable === void 0 ? void 0 : renderable.setVisible(false);
                    });
                    break;
                case "m":
                    // show all training videos
                    this.trainingVideoRenderables.forEach((renderable) => {
                        renderable === null || renderable === void 0 ? void 0 : renderable.setVisible(true);
                    });
                    break;
                default:
                    break;
            }
        };
        window.addEventListener("keydown", handleKeyDown);
    }
    updateVideoState(trainingVideoId, newState) {
        this.renderer.getConnection().send(new TrainingVideoStateChangedMessage(trainingVideoId, newState));
    }
    // video blob cache
    getOrFetchVideo(videoSrc) {
        return __awaiter(this, void 0, void 0, function* () {
            // 1. Fast path: blob URL already created this session (ETag was already validated or freshly fetched)
            const existing = this.videoBlobUrls.get(videoSrc);
            if (existing !== undefined) {
                return existing;
            }
            // 2. Check persistent IndexedDB cache and revalidate via ETag
            const cached = yield cacheService.getWithEtag(videoSrc);
            if (cached !== undefined && cached.data instanceof Blob && cached.etag !== undefined && cached.etag !== null && cached.etag !== "") {
                // Ask the server whether the video has changed
                const revalidation = yield fetch(videoSrc, {
                    headers: { "If-None-Match": cached.etag }
                });
                if (revalidation.status === 404)
                    return undefined; // Video no longer exists on server – treat as cache miss
                if (revalidation.status === 304) {
                    // Video unchanged – use cached blob
                    cacheService.usedCache(videoSrc);
                    const blobUrl = URL.createObjectURL(cached.data);
                    this.videoBlobUrls.set(videoSrc, blobUrl);
                    return blobUrl;
                }
                if (revalidation.ok) {
                    // Video changed – store new version
                    const blob = yield revalidation.blob();
                    const etag = revalidation.headers.get("etag");
                    yield cacheService.add(videoSrc, blob, etag);
                    const blobUrl = URL.createObjectURL(blob);
                    this.videoBlobUrls.set(videoSrc, blobUrl);
                    return blobUrl;
                }
            }
            // 3. No cache or no ETag – fetch from network
            const response = yield fetch(videoSrc);
            if (!response.ok) {
                return undefined;
            }
            const blob = yield response.blob();
            const etag = response.headers.get("etag");
            yield cacheService.add(videoSrc, blob, etag);
            const blobUrl = URL.createObjectURL(blob);
            this.videoBlobUrls.set(videoSrc, blobUrl);
            return blobUrl;
        });
    }
    // register/unregister
    register(trainingVideoRenderable) {
        if (trainingVideoRenderable.currentFacet !== undefined)
            this.trainingVideoRenderables.set(trainingVideoRenderable.currentFacet.id, trainingVideoRenderable);
    }
    unregister(trainingVideoRenderable) {
        if (trainingVideoRenderable.currentFacet !== undefined)
            this.trainingVideoRenderables.delete(trainingVideoRenderable.currentFacet.id);
    }
}
